import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * SideDrawer — slide-over desde la derecha, estilo iOS.
 * z-[70] para quedar sobre bottom nav (z-50) y sheets (z-60).
 */
export default function SideDrawer({ isOpen, onClose, title, children }) {
  const startXRef = useRef(null);
  const drawerRef = useRef(null);

  // Scroll lock — capture previous value so closing doesn't clobber a concurrent BottomSheet lock.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Swipe-right to close
  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (startXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - startXRef.current;
    if (deltaX > 60) onClose();
    startXRef.current = null;
  };

  // ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className={`
          fixed top-0 right-0 z-[70]
          h-full w-[88vw] max-w-[400px]
          bg-page-bg
          flex flex-col
          shadow-[-8px_0_40px_rgba(0,0,0,0.12)]
          transition-transform duration-300 ease-out
          ${isOpen ? "translate-x-0" : "translate-x-full"}
        `}
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-slate-200/60 dark:border-slate-700/60 shrink-0">
          <h2 className="text-[17px] font-bold text-slate-900 dark:text-slate-100">{title}</h2>
          <button
            onClick={onClose}
            className="h-11 w-11 rounded-full bg-slate-200/60 dark:bg-slate-700/60 flex items-center justify-center text-slate-500 dark:text-slate-400 transition-all duration-200 ease-out hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-[0.98]"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </>
  );
}
